import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import { Logger } from '@feature-hub/core';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';

import { AuthServiceV4 } from '@oneaudi/audi-auth-service';
import { ContentService } from '@oneaudi/content-service';
import { DealerContextServiceV1 } from '@oneaudi/dealer-context-service';
import { FootnoteContextProvider } from '@oneaudi/feature-app-utils';
import { AudiFootnoteRefernceServiceScopeManagerInterfaceV3 } from '@oneaudi/footnote-reference-service';
import { AudiFootnoteServiceInterfaceV1 } from '@oneaudi/footnote-service';
import { I18NServiceV1 } from '@oneaudi/i18n-service';
import { NumberFormatterServiceV1 } from '@oneaudi/number-formatter-service/dist/v1';
import { ApolloClient } from '@oneaudi/onegraph-client';
import { EcommerceServiceV1 } from '@volkswagen-onehub/e-commerce-service';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import React, { Dispatch, useEffect } from 'react';
import { normalizeConfig } from '~/app/FeatureHubAppDefinition';
import { windowAvailable } from '~/utils/client-detection';
import { useSafeContext } from '../hooks/use-safe-context';
import reducer, { Action, State } from './reducers/app-reducer';
import { ThemeProvider } from '@oneaudi/unified-web-alpha-common';

interface AppServices {
	featureServices: AppFeatureServices;
}

export interface AppFeatureServices {
	i18nService: I18NServiceV1;
	localeService: GfaLocaleServiceV1;
	loggerService: Logger | undefined;
	contentService: ContentService;
	footnoteReferenceService?: AudiFootnoteRefernceServiceScopeManagerInterfaceV3;
	footnoteService?: AudiFootnoteServiceInterfaceV1;
	trackingService?: TrackingServiceV2;
	asyncSSRManager?: AsyncSsrManagerV1;
	numberFormatterService: NumberFormatterServiceV1;
	eCommerceService: EcommerceServiceV1;
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	oneGraphClient: ApolloClient<any>;
	authService: AuthServiceV4;
	configProviderService?: GfaServiceConfigProviderV1;
	dealerContextService: DealerContextServiceV1;
}

export const AppStateContext = React.createContext<State | null>(null);
export const AppDispatchContext = React.createContext<Dispatch<Action> | null>(null);
export const AppServicesContext = React.createContext<AppServices | null>(null);

export function AppProvider({
	featureServices,
	initialState,
	children,
}: {
	featureServices: AppFeatureServices;
	initialState: State;
	children: React.ReactNode;
}) {
	const [state, dispatch] = React.useReducer(reducer, initialState);
	const { contentService } = featureServices;

	useEffect(() => {
		const listener = () => {
			if (windowAvailable()) {
				const params = new URLSearchParams(window?.location?.search);
				params.delete('pr');

				history.replaceState({}, '', `${window?.location?.pathname}?${params.toString()}`);
			}
			dispatch({
				type: 'SET_CONTENT',
				payload: normalizeConfig(contentService.getContent()) || initialState.content,
			});
		};
		contentService?.onContentChange(listener);

		return () => {
			contentService?.removeOnContentChange(listener);
		};
	}, [contentService]);

	const _footnoteReferenceService = featureServices.footnoteReferenceService?.getDefaultScopeRefService();

	return (
		<AppServicesContext.Provider
			value={{
				featureServices,
			}}>
			<AppStateContext.Provider value={state}>
				<AppDispatchContext.Provider value={dispatch}>
					<FootnoteContextProvider footnoteReferenceService={_footnoteReferenceService!}>
						<div data-fefa-custom-id={_footnoteReferenceService?.getConsumerId()}>
							<ThemeProvider theme={{ name: 'dark' }}>{children}</ThemeProvider>
						</div>
					</FootnoteContextProvider>
				</AppDispatchContext.Provider>
			</AppStateContext.Provider>
		</AppServicesContext.Provider>
	);
}

export function useAppState() {
	return useSafeContext(AppStateContext, 'AppState');
}

export function useAppDispatch() {
	return useSafeContext(AppDispatchContext, 'AppDispatch');
}

export function useAppServices() {
	return useSafeContext(AppServicesContext, 'AppServices');
}

export function useFeatureServices() {
	return useAppServices().featureServices;
}
